import ImageBlobReduce from 'image-blob-reduce';
import mime from '../../node_modules/mime-types/index.js';
import { fileTypeFromBuffer } from '../../node_modules/file-type/core.js';
import { isText } from '../../node_modules/istextorbinary/edition-browsers/index.js';
import { ensureError } from '../guards/index.js';

const mimeToKind = {
  "application/pdf": "PDF document",
  "application/msword": "Microsoft Word document",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "Microsoft Word document (DOCX)",
  "application/vnd.ms-excel": "Microsoft Excel spreadsheet",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "Microsoft Excel spreadsheet (XLSX)",
  "application/vnd.ms-powerpoint": "Microsoft PowerPoint presentation",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "Microsoft PowerPoint presentation (PPTX)",
  "application/zip": "ZIP archive",
  "application/x-rar-compressed": "RAR archive",
  "application/x-7z-compressed": "7-Zip archive",
  "application/json": "JSON file",
  "application/xml": "XML file",
  "application/javascript": "JavaScript file",
  "application/octet-stream": "Binary file",
  "text/plain": "Plain Text file",
  "text/html": "HTML file",
  "text/css": "CSS file",
  "text/csv": "CSV file",
  "text/markdown": "Markdown file",
  "image/jpeg": "JPEG image",
  "image/png": "PNG image",
  "image/gif": "GIF image",
  "image/svg+xml": "SVG image",
  "image/webp": "WebP image",
  "image/tiff": "TIFF image",
  "image/bmp": "BMP image",
  "image/heic": "HEIC image",
  "image/heif": "HEIF image",
  "image/avif": "AVIF image",
  "audio/mpeg": "MP3 audio",
  "audio/wav": "WAV audio",
  "audio/ogg": "OGG audio",
  "audio/midi": "MIDI audio",
  "audio/webm": "WebM audio",
  "video/mp4": "MP4 video",
  "video/mpeg": "MPEG video",
  "video/quicktime": "QuickTime video",
  "video/webm": "WebM video",
  "video/x-msvideo": "AVI video",
  "application/vnd.android.package-archive": "Android package",
  "application/vnd.apple.installer+xml": "Apple Installer package",
  "application/x-shockwave-flash": "Adobe Flash",
  "application/vnd.visio": "Microsoft Visio document",
  "application/rtf": "Rich Text Format",
  "application/x-font-ttf": "TrueType font",
  "application/x-font-otf": "OpenType font",
  "application/vnd.ms-fontobject": "Embedded OpenType font",
  "application/x-bittorrent": "BitTorrent file",
  "application/x-tex": "TeX document",
  "application/graphql": "GraphQL",
  "application/sql": "SQL file",
  "application/x-httpd-php": "PHP script",
  "application/x-python-code": "Python script",
  "application/vnd.apple.keynote": "Apple Keynote presentation",
  "application/vnd.apple.pages": "Apple Pages document",
  "application/vnd.apple.numbers": "Apple Numbers spreadsheet",
  "application/vnd.oasis.opendocument.text": "OpenDocument Text",
  "application/vnd.oasis.opendocument.spreadsheet": "OpenDocument spreadsheet",
  "application/vnd.oasis.opendocument.presentation": "OpenDocument presentation",
  "application/epub+zip": "EPUB E-book",
  "application/x-photoshop": "Adobe Photoshop document",
  "application/postscript": "PostScript file",
  "application/x-font-woff": "WOFF font",
  "application/x-font-woff2": "WOFF2 font",
  "application/x-yaml": "YAML file",
  "application/x-sh": "Shell script",
  "application/x-binary": "Binary executable",
  "text/xml": "XML text",
  "text/rtf": "Rich Text Format",
  "image/x-icon": "ICO image",
  "image/vnd.adobe.photoshop": "Photoshop document",
  "audio/aac": "AAC audio",
  "video/x-matroska": "Matroska video",
  "video/matroska": "Matroska video",
  "application/vnd.fabric.notepad": "Fabric note",
  "application/vnd.fabric.highlight": "Highlight",
  "application/vnd.fabric.bookmark": "Bookmark"
};
const generateKind = (mime2, extension) => {
  if (mime2 in mimeToKind) {
    return mimeToKind[mime2];
  }
  if (!extension) {
    return "";
  }
  const cleanExtension = extension.startsWith(".") ? extension.slice(1) : extension;
  return `${cleanExtension.toUpperCase()} file`;
};
function stripBase64Prefix(base64String) {
  return base64String.replace(/^data:[^;]+;base64,/, "");
}
function base64ToUint8Array(base64String, maxBytes) {
  const base64Data = base64String.replace(/^data:[^;]+;base64,/, "");
  const bytesToProcess = maxBytes ? Math.min(Math.ceil(maxBytes * 4 / 3), base64Data.length) : base64Data.length;
  const processedBase64 = base64Data.slice(0, bytesToProcess);
  const binaryString = atob(processedBase64);
  const len = Math.min(binaryString.length, maxBytes ?? Infinity);
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}
const blobToBase64 = async (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
      } else {
        reject(new Error("Failed to convert blob to base64"));
      }
    };
    reader.onerror = () => {
      reject(new Error("Failed to read blob"));
    };
    reader.readAsDataURL(blob);
  });
};
const validateImageUrl = async (url) => {
  if (!url) {
    return null;
  }
  try {
    const res = await fetch(url, {
      method: "HEAD"
    });
    if (res.status !== 200) {
      return null;
    }
    const contentType = res.headers.get("content-type");
    if (!contentType?.startsWith("image/")) {
      return null;
    }
    return await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve(url);
      };
      img.onerror = () => {
        resolve(null);
      };
      img.src = url;
    });
  } catch {
    return null;
  }
};
const resizeImage = async (file, maxSize = 512) => {
  const resizer = new ImageBlobReduce();
  resizer._create_blob = async function(env) {
    if (!env.out_canvas) return env;
    return this.pica.toBlob(env.out_canvas, "image/jpeg", 0.8).then(function(blob) {
      env.out_blob = blob;
      return env;
    });
  };
  try {
    const result = await resizer.toBlob(file, {
      max: maxSize
    });
    return result;
  } catch (error) {
    console.error("Error resizing image:", error);
    throw ensureError(error);
  }
};
const sanitizeFilename = (filename) => {
  return filename.replace(/[^A-Za-z0-9._-]+/g, "_").replace(/^_+|_+$/g, "").replace(/^-+/, "").replace(/_+/g, "_");
};
const getImageNaturalDimensions = async (image) => {
  const img = new Image();
  img.src = typeof image === "string" ? image : URL.createObjectURL(image);
  return new Promise((resolve) => {
    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight
      });
    };
  });
};
const isObjectUrl = (url) => {
  try {
    const urlObj = new URL(url);
    return urlObj.protocol === "blob:";
  } catch {
    return false;
  }
};
const revokeUrlIfObjectUrl = (url) => {
  try {
    if (isObjectUrl(url)) {
      URL.revokeObjectURL(url);
    }
  } catch {
  }
};
function stripExtension(filename) {
  const split = filename.split(".");
  if (split.length === 1) {
    return filename;
  }
  return split.slice(0, -1).join(".");
}
const getFileNameFromPath = (path) => {
  const split = path.split("/");
  return split[split.length - 1];
};
const correctMimeTypeMap = {
  "text/x-markdown": "text/markdown"
};
const invalidMimeTypes = ["application/octet-stream", "application/x-msdownload", "", "*/*", "application/x-unknown-content-type", "application/unknown", "application/force-download", "text/plain"];
const correctMimeType = (file) => {
  if (invalidMimeTypes.includes(file.type)) {
    const correctedType = correctMimeTypeMap[file.type];
    if (correctedType) {
      return new File([file], file.name, {
        type: correctedType
      });
    }
  }
  return file;
};
const hasExtension = (fileName) => {
  const split = fileName.split(".");
  return split.length > 1;
};
const updateFileMimeType = async (file) => {
  if (!invalidMimeTypes.includes(file.type)) {
    return correctMimeType(file);
  }
  const arrayBuffer = await file.arrayBuffer();
  const uint8Array = new Uint8Array(arrayBuffer);
  const buffer = Buffer.from(uint8Array);
  const fileName = file.name;
  if (isText(fileName, buffer)) {
    const mimeType = mime.lookup(fileName) || "text/plain";
    return correctMimeType(new File([file], fileName, {
      type: mimeType
    }));
  } else {
    const limitedBinary = uint8Array.slice(0, Math.min(4100, uint8Array.length));
    const parsedFileType = await fileTypeFromBuffer(limitedBinary);
    if (parsedFileType) {
      const extension = parsedFileType.ext;
      const mimeType2 = parsedFileType.mime;
      const updatedName = hasExtension(fileName) ? fileName : `${fileName}.${extension}`;
      return correctMimeType(new File([file], updatedName, {
        type: mimeType2
      }));
    }
    const mimeType = mime.lookup(fileName) || "application/octet-stream";
    return correctMimeType(new File([file], fileName, {
      type: mimeType
    }));
  }
};
const byteToMb = (bytes) => {
  return bytes / 1024 / 1024;
};

export { base64ToUint8Array, blobToBase64, byteToMb, generateKind, getFileNameFromPath, getImageNaturalDimensions, isObjectUrl, resizeImage, revokeUrlIfObjectUrl, sanitizeFilename, stripBase64Prefix, stripExtension, updateFileMimeType, validateImageUrl };
//# sourceMappingURL=index.js.map
